import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, KeyRound, Building2, Sofa, Link as LinkIcon, Printer, ExternalLink, UserRound, Phone } from "lucide-react";
import { Layout, Brand } from "@/components/Layout";

const ADDRESS = "44 rue Jean Mermoz, 78600 Maisons-Laffitte";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=44+rue+Jean+Mermoz+78600+Maisons-Laffitte";

const STEPS = [
  {
    key: "hall",
    icon: Building2,
    image: "/infos/batiment.jpg",
    title: "Accès au bâtiment — Hall B",
    sub: "CAP MERMOZ · 44",
    text: "À l'interphone, tapez le code pour déverrouiller la porte du hall.",
    code: "6519",
  },
  {
    key: "ascenseur",
    icon: KeyRound,
    image: "/infos/hall.jpg",
    title: "Ascenseur — Centre Stop & Work",
    sub: "Boîtier noir dans l'ascenseur",
    text: "Composez le code sur le boîtier noir, puis montez au 1ᵉʳ étage.",
    code: "5223#",
  },
  {
    key: "attente",
    icon: Sofa,
    image: "/infos/attente.jpg",
    title: "Salle d'attente",
    sub: "À la sortie de l'ascenseur",
    text: "La salle d'attente se trouve juste à la sortie de l'ascenseur. Veuillez patienter : M. Hatti viendra se présenter et vous accompagnera jusqu'à l'espace de consultation.",
    code: null,
  },
];

const PHONE = "01 30 08 30 10";
const PHONE_TEL = "+33130083010";

export default function InfosPratiques() {
  const [url] = useState(() => (typeof window !== "undefined" ? window.location.href.split("?")[0] : ""));

  useEffect(() => {
    const prev = document.title;
    document.title = "M2HAT — Informations pratiques";
    return () => { document.title = prev; };
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié", { description: url });
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  return (
    <Layout>
      <div className="print-container animate-fade-up" data-testid="infos-pratiques">
        <div className="hidden print:block mb-6"><Brand /></div>

        {/* Hero */}
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold-light)] mb-4">Identifier · Calmer · Transformer</p>
          <h1 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl leading-tight mb-3 bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(100deg, var(--gold) 10%, var(--gold-light) 55%, #c99a63 90%)" }}>Informations pratiques</h1>
          <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">
            Préparez votre arrivée au cabinet · Maisons-Laffitte (78)
          </p>
        </div>

        {/* Adresse */}
        <div className="rounded-3xl text-white p-6 sm:p-8 mb-10 text-center print-break shadow-sm" style={{ backgroundImage: "linear-gradient(135deg, #8a6a44 0%, var(--gold) 45%, var(--gold-light) 100%)" }} data-testid="infos-address">
          <div className="flex items-center justify-center gap-2 mb-2">
            <MapPin size={20} />
            <span className="text-xs uppercase tracking-[0.2em] text-white/85">Adresse du cabinet</span>
          </div>
          <p className="font-serif-title text-2xl sm:text-3xl leading-snug">{ADDRESS}</p>
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="infos-maps-link"
            className="inline-flex items-center gap-1.5 mt-4 text-sm bg-white/15 hover:bg-white/25 transition-colors rounded-full px-4 py-2 no-print"
          >
            <ExternalLink size={15} /> Itinéraire (Google Maps)
          </a>
        </div>

        {/* Étapes */}
        <div className="space-y-6 mb-12">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.key}
                data-testid={`infos-step-${step.key}`}
                className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] overflow-hidden print-break"
              >
                {step.image && (
                  <img src={step.image} alt={step.title} loading="lazy" data-testid={`infos-photo-${step.key}`} className="w-full h-56 sm:h-72 object-cover" />
                )}
                <div className="p-5 sm:p-7 flex gap-4 sm:gap-5 items-start">
                  <div className="shrink-0 flex flex-col items-center gap-2">
                    <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[var(--gold)] text-white font-serif-title text-xl shadow-sm">
                      {i + 1}
                    </span>
                    <Icon size={22} className="text-[var(--gold)]" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-serif-title text-2xl sm:text-[1.6rem] text-[var(--gold)] leading-tight">{step.title}</h2>
                    {step.sub && <p className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)] mt-1 mb-2">{step.sub}</p>}
                    <p className="text-sm sm:text-base text-[var(--ink)] leading-relaxed">{step.text}</p>
                    {step.code && (
                      <div className="mt-4 inline-flex items-center gap-3 rounded-2xl bg-white border border-[var(--border)] px-5 py-3">
                        <span className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)]">Code</span>
                        <span className="font-mono text-2xl sm:text-3xl tracking-[0.25em] text-[var(--gold)] font-bold" data-testid={`infos-code-${step.key}`}>{step.code}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Note d'accueil + contact */}
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 text-center mb-10 print-break" data-testid="infos-help">
          <UserRound size={22} className="text-[var(--gold)] mx-auto mb-2" />
          <p className="text-[var(--ink)] leading-relaxed max-w-lg mx-auto mb-4">
            À très bientôt au cabinet. En cas de difficulté d'accès le jour du rendez-vous, contactez le psychologue / l'accueil.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="infos-phone"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)] text-white px-6 py-3 font-medium hover:brightness-105 transition-all"
          >
            <Phone size={17} /> {PHONE}
          </a>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-center gap-4 no-print">
          <button
            onClick={() => window.print()}
            data-testid="infos-btn-pdf"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all"
          >
            <Printer size={17} /> Exporter en PDF
          </button>
          <button
            onClick={copyLink}
            data-testid="infos-btn-copy"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors"
          >
            <LinkIcon size={16} /> Copier le lien à partager
          </button>
        </div>
        <span className="sr-only" data-testid="infos-share-url">{url}</span>
      </div>
    </Layout>
  );
}
